import React, { useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  Chip,
  CircularProgress,
  Container,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  alpha,
} from '@mui/material';
import { Icon } from '@iconify/react';
import { useNavigate } from 'react-router-dom';
import { useMutation } from '@tanstack/react-query';
import { toast } from 'react-toastify';
import Page from 'src/components/Page';
import {
  ROSTER_CSV_TEMPLATE,
  downloadTextFile,
  payrollImportService,
  PAYROLL_CONFIG,
} from 'src/services/payroll';
import type { RosterCreateResult, RosterPreviewRow } from 'src/types/payroll.types';
import { CsvDropzone } from './components/CsvDropzone';
import { WizardStepper } from './components/PaySummaryCards';

export default function RosterImport() {
  const navigate = useNavigate();
  const [fileName, setFileName] = useState<string | null>(null);
  const [csvText, setCsvText] = useState<string | null>(null);
  const [preview, setPreview] = useState<RosterPreviewRow[] | null>(null);
  const [result, setResult] = useState<RosterCreateResult | null>(null);
  const [step, setStep] = useState(0);

  const previewMutation = useMutation({
    mutationFn: async () => {
      if (!csvText) throw new Error('Upload a student account CSV first');
      return payrollImportService.previewRoster(csvText);
    },
    onSuccess: (rows) => {
      setPreview(rows);
      setStep(1);
      toast.success('Student account preview ready');
    },
    onError: (err: any) => toast.error(err?.message || 'Preview failed'),
  });

  const createMutation = useMutation({
    mutationFn: async () => {
      if (!preview) throw new Error('No preview');
      return payrollImportService.createRosterStudents(preview);
    },
    onSuccess: (res) => {
      setResult(res);
      setStep(2);
      const parts = [];
      if (res.created > 0) parts.push(`created ${res.created}`);
      if ((res.updated || 0) > 0) parts.push(`updated ${res.updated}`);
      if (parts.length > 0) {
        toast.success(`Successfully ${parts.join(' and ')} student account${res.created + (res.updated || 0) === 1 ? '' : 's'}`);
      }
      if (res.failed > 0) toast.warning(`${res.failed} failed to process`);
    },
    onError: (err: any) => toast.error(err?.message || 'Processing failed'),
  });

  const counts = useMemo(() => {
    const rows = preview || [];
    return {
      create: rows.filter((r) => r.status === 'create').length,
      update: rows.filter((r) => r.status === 'update').length,
      skip: rows.filter((r) => r.status === 'skip_exists').length,
      error: rows.filter((r) => r.status === 'error').length,
    };
  }, [preview]);

  const downloadCredentials = () => {
    if (!result?.credentials?.length) return;
    const csvContent = payrollImportService.exportCredentialsCsv(result.credentials);
    downloadTextFile(`student-account-credentials-${Date.now()}.csv`, csvContent);
    toast.success('Credentials CSV downloaded. All new student passwords match their Student IDs.');
  };

  return (
    <Page title="Student Account Import">
      <Container
        maxWidth={false}
        sx={{ width: '100%', px: 1, mx: 0 }}
      >
        <Button
          startIcon={<Icon icon="mdi:arrow-left" />}
          onClick={() => navigate('/admin/payroll')}
          sx={{ textTransform: 'none', mb: 0.25, color: 'text.secondary', p: 0, fontSize: '0.75rem' }}
        >
          Scholar Dollar Pay
        </Button>

        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          justifyContent="space-between"
          alignItems={{ xs: 'flex-start', sm: 'center' }}
          sx={{ mb: 1.5 }}
          spacing={1}
        >
          <Box>
            <Typography variant="h5" fontWeight={800} color="#1A202C">
              Student Account Setup
            </Typography>
            <Typography variant="caption" color="text.secondary">
              Create student accounts from a CSV and connect Student IDs for payroll matching.
            </Typography>
          </Box>
          <Button
            startIcon={<Icon icon="mdi:download" />}
            sx={{ textTransform: 'none', fontWeight: 600 }}
            onClick={() => downloadTextFile('student-account-template.csv', ROSTER_CSV_TEMPLATE)}
          >
            Download student template
          </Button>
        </Stack>

        <WizardStepper steps={['Upload', 'Preview', 'Credentials']} activeStep={step} />

        {step === 0 && (
          <Card elevation={0} sx={{ p: { xs: 1.75, sm: 2.25 }, borderRadius: 2.5, border: '1px solid #E2E8F0' }}>
            <Alert severity="info" icon={<Icon icon="mdi:information-outline" width={22} />} sx={{ mb: 2.5, borderRadius: 2 }}>
              If student emails are provided in the CSV (e.g. <code>email</code> column), they will be used.
              Otherwise, login emails default to{' '}
              <code>first.last@{PAYROLL_CONFIG.rosterEmailDomain}</code> (or <code>&#123;student_id&#125;@{PAYROLL_CONFIG.rosterEmailDomain}</code>).
              Each student's initial password is automatically set to their <strong>Student ID</strong>.
            </Alert>
            <CsvDropzone
              acceptLabel="Student account CSV with student_id, first_name, last_name, grade_level, email (optional)"
              fileName={fileName}
              onFile={(file, text) => {
                setFileName(file.name);
                setCsvText(text);
              }}
            />
            <Stack direction="row" justifyContent="flex-end" sx={{ mt: 3 }}>
              <Button
                variant="contained"
                disabled={!csvText || previewMutation.isPending}
                onClick={() => previewMutation.mutate()}
                startIcon={
                  previewMutation.isPending ? (
                    <CircularProgress size={18} color="inherit" />
                  ) : (
                    <Icon icon="mdi:eye-check-outline" />
                  )
                }
                sx={{
                  textTransform: 'none',
                  fontWeight: 700,
                  borderRadius: 2,
                  bgcolor: '#2B6CB0',
                  '&:hover': { bgcolor: '#2C5282' },
                }}
              >
                Preview students
              </Button>
            </Stack>
          </Card>
        )}

        {step >= 1 && preview && (
          <Card elevation={0} sx={{ borderRadius: 3, border: '1px solid #E2E8F0', overflow: 'hidden' }}>
            <Stack direction="row" spacing={1.5} sx={{ p: { xs: 1.75, sm: 2.25 } }} flexWrap="wrap">
              <Chip color="success" label={`${counts.create} will create`} sx={{ fontWeight: 700 }} />
              {counts.update > 0 && (
                <Chip color="info" label={`${counts.update} will update email/info`} sx={{ fontWeight: 700 }} />
              )}
              <Chip color="default" label={`${counts.skip} unchanged`} sx={{ fontWeight: 600 }} />
              <Chip color="error" label={`${counts.error} errors`} sx={{ fontWeight: 600 }} />
            </Stack>

            <TableContainer sx={{ maxHeight: 480 }}>
              <Table stickyHeader size="small">
                <TableHead>
                  <TableRow>
                    {['#', 'Student ID', 'Name', 'Grade', 'Email', 'Status'].map((h) => (
                      <TableCell key={h} sx={{ fontWeight: 700, bgcolor: alpha('#2B6CB0', 0.04) }}>
                        {h}
                      </TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {preview.map((row) => (
                    <TableRow key={`${row.rowNumber}-${row.externalStudentId}`} hover>
                      <TableCell>{row.rowNumber}</TableCell>
                      <TableCell sx={{ fontFamily: 'monospace' }}>{row.externalStudentId}</TableCell>
                      <TableCell>
                        {row.firstName} {row.lastName}
                      </TableCell>
                      <TableCell>{row.gradeLevel || '—'}</TableCell>
                      <TableCell>{row.email || '—'}</TableCell>
                      <TableCell>
                        <Chip
                          size="small"
                          label={row.message || row.status}
                          color={
                            row.status === 'create'
                              ? 'success'
                              : row.status === 'update'
                                ? 'info'
                                : row.status === 'error'
                                  ? 'error'
                                  : 'default'
                          }
                          sx={{ fontWeight: 600 }}
                        />
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>

            {step === 1 && (
              <Stack
                direction="row"
                justifyContent="space-between"
                sx={{ p: { xs: 1.75, sm: 2.25 }, borderTop: '1px solid #E2E8F0', bgcolor: alpha('#2B6CB0', 0.03) }}
              >
                <Button sx={{ textTransform: 'none' }} onClick={() => setStep(0)}>
                  Back
                </Button>
                <Button
                  variant="contained"
                  disabled={counts.create === 0 && counts.update === 0 || createMutation.isPending}
                  onClick={() => createMutation.mutate()}
                  startIcon={
                    createMutation.isPending ? (
                      <CircularProgress size={18} color="inherit" />
                    ) : (
                      <Icon icon="mdi:account-multiple-plus" />
                    )
                  }
                  sx={{
                    textTransform: 'none',
                    fontWeight: 700,
                    borderRadius: 2,
                    bgcolor: '#2B6CB0',
                  }}
                >
                  {createMutation.isPending
                    ? 'Processing accounts…'
                    : counts.create > 0 && counts.update > 0
                      ? `Process ${counts.create + counts.update} students (${counts.create} new, ${counts.update} updates)`
                      : counts.create > 0
                        ? `Create ${counts.create} student${counts.create === 1 ? '' : 's'}`
                        : `Update ${counts.update} student${counts.update === 1 ? '' : 's'}`}
                </Button>
              </Stack>
            )}

            {step === 2 && result && (
              <Box sx={{ p: 3, borderTop: '1px solid #E2E8F0' }}>
                <Alert
                  severity={result.failed ? 'warning' : 'success'}
                  sx={{ mb: 2, borderRadius: 2 }}
                >
                  Created {result.created}, updated {result.updated || 0}, unchanged {result.skipped}, failed {result.failed}.
                </Alert>

                <Alert
                  severity="info"
                  icon={<Icon icon="mdi:shield-key-outline" width={24} />}
                  sx={{ mb: 2.5, borderRadius: 2 }}
                >
                  <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 0.5 }}>
                    Student Password Notice
                  </Typography>
                  <Typography variant="body2">
                    All student passwords are set to match their <strong>Student ID</strong> (e.g., password = Student ID).
                    Students can sign in using their email and their Student ID as their password.
                  </Typography>
                </Alert>

                <Stack direction="row" spacing={1.5} sx={{ mb: result.credentials.length ? 3 : 0 }}>
                  <Button
                    variant="contained"
                    disabled={!result.credentials.length}
                    onClick={downloadCredentials}
                    startIcon={<Icon icon="mdi:download" />}
                    sx={{
                      textTransform: 'none',
                      fontWeight: 700,
                      borderRadius: 2,
                      bgcolor: '#EDB42E',
                      color: '#1A1A1A',
                      '&:hover': { bgcolor: '#d9a429' },
                    }}
                  >
                    Download credentials CSV
                  </Button>
                  <Button sx={{ textTransform: 'none' }} onClick={() => navigate('/admin/payroll')}>
                    Done
                  </Button>
                </Stack>

                {result.credentials.length > 0 && (
                  <Box sx={{ mt: 2 }}>
                    <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1 }}>
                      Account Credentials Summary
                    </Typography>
                    <TableContainer sx={{ maxHeight: 320, borderRadius: 2, border: '1px solid #E2E8F0' }}>
                      <Table stickyHeader size="small">
                        <TableHead>
                          <TableRow>
                            {['Student ID', 'Name', 'Email', 'Password (Student ID)', 'Status'].map((h) => (
                              <TableCell key={h} sx={{ fontWeight: 700, bgcolor: alpha('#2B6CB0', 0.04) }}>
                                {h}
                              </TableCell>
                            ))}
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {result.credentials.map((cred) => (
                            <TableRow key={`${cred.externalStudentId}-${cred.email}`} hover>
                              <TableCell sx={{ fontFamily: 'monospace', fontWeight: 600 }}>
                                {cred.externalStudentId}
                              </TableCell>
                              <TableCell>
                                {cred.firstName} {cred.lastName}
                              </TableCell>
                              <TableCell>{cred.email}</TableCell>
                              <TableCell>
                                {cred.action === 'updated' ? (
                                  <Typography variant="caption" color="text.secondary">
                                    Unchanged
                                  </Typography>
                                ) : (
                                  <Box
                                    component="code"
                                    sx={{
                                      bgcolor: alpha('#297041', 0.08),
                                      color: '#297041',
                                      px: 1,
                                      py: 0.25,
                                      borderRadius: 1,
                                      fontWeight: 700,
                                      fontFamily: 'monospace',
                                    }}
                                  >
                                    {cred.password || cred.tempPassword || cred.externalStudentId}
                                  </Box>
                                )}
                              </TableCell>
                              <TableCell>
                                <Chip
                                  size="small"
                                  label={cred.action === 'updated' ? 'Updated' : 'Created'}
                                  color={cred.action === 'updated' ? 'info' : 'success'}
                                  sx={{ fontWeight: 600 }}
                                />
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  </Box>
                )}

                {result.failures.length > 0 && (
                  <Box sx={{ mt: 2.5 }}>
                    <Typography fontWeight={700} gutterBottom color="error">
                      Failures ({result.failures.length})
                    </Typography>
                    {result.failures.map((f) => (
                      <Typography key={f.externalStudentId} variant="body2" color="error">
                        {f.externalStudentId}: {f.error}
                      </Typography>
                    ))}
                  </Box>
                )}
              </Box>
            )}
          </Card>
        )}
      </Container>
    </Page>
  );
}
